import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import { subscribeApiHealth } from "@/lib/apiHealth";
import { isLoggedIn as authIsLoggedIn, getRole, isAllowedForRole, landingPath } from "@/lib/auth";

// Sticky warning while the backend is unreachable — pages silently fall back
// to bundled demo data, which must never be mistaken for business state.
function ApiDownBanner() {
  const [degraded, setDegraded] = useState(false);
  useEffect(() => subscribeApiHealth(setDegraded), []);
  if (!degraded) return null;
  return (
    <div className="bg-red-600 text-white text-sm font-medium px-4 py-2.5 flex items-center gap-2 shadow">
      <i className="ri-error-warning-fill text-base" />
      后端连接中断（API 不可用）——当前页面展示的是内置演示数据，请勿据此判断业务状态；恢复后将自动消除此提示
    </div>
  );
}

function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  if (!authIsLoggedIn()) {
    return <Navigate to="/" replace />;
  }

  // ops 只能进入租借相关页面，访问其他路径一律弹回租借首页。
  const role = getRole();
  if (!isAllowedForRole(role, location.pathname)) {
    return <Navigate to={landingPath(role)} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — hidden off-screen on mobile, always visible on desktop */}
      <div
        className={`fixed left-0 top-0 h-screen transition-transform duration-300 z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <Sidebar onCloseMobile={() => setSidebarOpen(false)} />
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        <TopBar onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 mt-16 p-4 md:p-6">
          <div className="-mx-4 md:-mx-6 -mt-4 md:-mt-6 mb-4 sticky top-16 z-40">
            <ApiDownBanner />
          </div>
          <div className="w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;